import {
  Directive,
  Input,
  OnInit,
  TemplateRef,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { PermissionsService } from '@joyinvoice/web/shared/data-access';

/**
 * Structural directive that shows/hides template content based on a permission.
 *
 * Usage:
 *   <button *hasPermission="'invoices:create'">New Invoice</button>
 *   <div *hasPermission="'users:delete'; else noAccess">Delete button here</div>
 *
 * Multiple permissions (requires ALL):
 *   <button *hasPermission="['invoices:create', 'invoices:send']">Send</button>
 */
@Directive({
  selector: '[hasPermission]',
  standalone: true,
})
export class HasPermissionDirective implements OnInit {
  private readonly tpl = inject(TemplateRef<unknown>);
  private readonly vcr = inject(ViewContainerRef);
  private readonly permService = inject(PermissionsService);

  private _permission: string | string[] = '';
  private _elseRef: TemplateRef<unknown> | null = null;

  @Input()
  set hasPermission(value: string | string[]) {
    this._permission = value;
    this._update();
  }

  @Input()
  set hasPermissionElse(tpl: TemplateRef<unknown>) {
    this._elseRef = tpl;
    this._update();
  }

  ngOnInit(): void {
    this._update();
  }

  private _update(): void {
    const allowed = this._check();
    this.vcr.clear();

    if (allowed) {
      this.vcr.createEmbeddedView(this.tpl);
    } else if (this._elseRef) {
      this.vcr.createEmbeddedView(this._elseRef);
    }
  }

  private _check(): boolean {
    if (!this._permission || (Array.isArray(this._permission) && this._permission.length === 0)) {
      return true;
    }
    if (Array.isArray(this._permission)) {
      return this.permService.canAll(...this._permission);
    }
    return this.permService.can(this._permission);
  }
}
